import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';
import {
  PublicBookingConfirmation,
  PublicBookingRequest,
  PublicOperator,
  PublicTrip,
  PublicTripSearchFilters,
  PublicTripStop
} from '../../core/models/public-booking.model';
import { AuthService } from '../../core/services/auth.service';
import { PublicBookingService } from '../../core/services/public-booking.service';
import { FEATURE_KEYS } from '../../core/models/feature-flag.model';
import { FeatureFlagService } from '../../core/services/feature-flag.service';

@Component({
  selector: 'app-passenger-trip-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './passenger-trip-search.component.html',
  styleUrls: ['./passenger-trip-search.component.css']
})
export class PassengerTripSearchComponent implements OnInit {
  private fb = inject(FormBuilder);
  private publicBookingService = inject(PublicBookingService);
  private authService = inject(AuthService);
  private featureFlags = inject(FeatureFlagService);

  searchForm = this.fb.group({
    q: [''],
    operatorName: [''],
    operatorId: [null as number | null],
    routeOrStop: [''],
    date: [''],
    time: ['']
  });

  bookingForm = this.fb.group({
    passengerName: ['', Validators.required],
    passengerEmail: [''],
    passengerPhone: [''],
    seatsBooked: [1, [Validators.required, Validators.min(1)]]
  });

  lookupForm = this.fb.group({
    bookingReference: ['']
  });

  operators: PublicOperator[] = [];
  trips: PublicTrip[] = [];
  selectedTrip: PublicTrip | null = null;
  confirmation: PublicBookingConfirmation | null = null;
  lookupResult: PublicBookingConfirmation | null = null;
  selectedSeats: number[] = [];
  seatSelectionMessage = '';
  searchError = '';
  detailError = '';
  bookingError = '';
  lookupError = '';
  isLoadingOperators = false;
  isSearching = false;
  isLoadingDetail = false;
  isBooking = false;
  hasSearched = false;
  passengerBookingEnabled = false;
  featureStateLoaded = false;

  ngOnInit(): void {
    this.loadOperators();
    this.searchTrips();
    this.featureFlags.publicFeatures$.subscribe(states => {
      this.passengerBookingEnabled = states[FEATURE_KEYS.passengerBooking] === true;
    });
    this.featureFlags.loadPublicFeatures().subscribe(() => this.featureStateLoaded = true);
    this.featureFlags.featureError$.subscribe(problem => {
      if (problem?.featureKey === FEATURE_KEYS.passengerBooking) {
        this.passengerBookingEnabled = false;
        this.featureStateLoaded = true;
        this.bookingError = problem.detail;
      }
    });
    this.bookingForm.get('seatsBooked')?.valueChanges.subscribe(value => this.onSeatCountChanged(Number(value || 0)));
  }

  loadOperators(): void {
    this.isLoadingOperators = true;
    this.publicBookingService.getOperators(this.currentFilters()).pipe(
      finalize(() => this.isLoadingOperators = false)
    ).subscribe({
      next: operators => this.operators = operators,
      error: () => this.searchError = 'Unable to load operators right now.'
    });
  }

  searchTrips(): void {
    this.isSearching = true;
    this.hasSearched = true;
    this.searchError = '';
    this.selectedTrip = null;
    this.confirmation = null;

    const filters = this.currentFilters();
    this.publicBookingService.getTrips(filters).pipe(
      finalize(() => this.isSearching = false)
    ).subscribe({
      next: trips => this.trips = this.orderTrips(trips),
      error: error => this.searchError = this.extractError(error, 'Unable to search trips right now.')
    });

    this.publicBookingService.getOperators(filters).subscribe({
      next: operators => this.operators = operators,
      error: () => undefined
    });
  }

  clearFilters(): void {
    this.searchForm.reset({
      q: '',
      operatorName: '',
      operatorId: null,
      routeOrStop: '',
      date: '',
      time: ''
    });
    this.searchTrips();
  }

  viewTrip(trip: PublicTrip): void {
    const tripId = this.getTripId(trip);
    if (!tripId) {
      this.detailError = 'Trip detail is unavailable for this result.';
      return;
    }

    this.isLoadingDetail = true;
    this.detailError = '';
    this.bookingError = '';
    this.confirmation = null;
    this.selectedSeats = [];
    this.seatSelectionMessage = '';

    this.publicBookingService.getTrip(tripId).pipe(
      finalize(() => this.isLoadingDetail = false)
    ).subscribe({
      next: detail => {
        this.selectedTrip = detail;
        this.resetBookingForm();
      },
      error: error => this.detailError = this.extractError(error, 'Unable to load trip detail.')
    });
  }

  toggleSeat(seatNumber: number): void {
    if (!this.passengerBookingEnabled) return;
    if (this.isSeatBooked(seatNumber)) {
      return;
    }

    this.bookingError = '';
    this.seatSelectionMessage = '';
    this.selectedSeats = this.selectedSeats.includes(seatNumber)
      ? this.selectedSeats.filter(item => item !== seatNumber)
      : [...this.selectedSeats, seatNumber].sort((a, b) => a - b);
    this.bookingForm.get('seatsBooked')?.setValue(Math.max(1, this.selectedSeats.length), { emitEvent: false });
  }

  onSeatCountChanged(count: number): void {
    if (!Number.isFinite(count) || count < 1) {
      this.selectedSeats = [];
      this.seatSelectionMessage = '';
      return;
    }

    if (count < this.selectedSeats.length) {
      this.selectedSeats = this.selectedSeats.slice(0, count);
      this.seatSelectionMessage = `Seat selection reduced to ${count}.`;
      return;
    }

    if (count > this.selectedSeats.length) {
      this.seatSelectionMessage = `Select ${count - this.selectedSeats.length} more seat${count - this.selectedSeats.length === 1 ? '' : 's'}, or continue without specific seat numbers.`;
      return;
    }

    this.seatSelectionMessage = '';
  }

  bookSelectedTrip(): void {
    if (!this.passengerBookingEnabled) {
      this.bookingError = 'Online booking is currently unavailable. You can still view available trips.';
      return;
    }
    this.bookingForm.markAllAsTouched();
    this.bookingError = '';

    if (!this.selectedTrip) {
      return;
    }

    if (!this.hasPassengerContact()) {
      this.bookingError = 'Enter an email or phone number so the booking can be recovered.';
      return;
    }

    if (this.bookingForm.invalid) {
      return;
    }

    const seatsBooked = Number(this.bookingForm.value.seatsBooked || 1);
    const payload: PublicBookingRequest = {
      seatsBooked,
      passengerName: String(this.bookingForm.value.passengerName || '').trim(),
      passengerEmail: this.nullIfBlank(this.bookingForm.value.passengerEmail),
      passengerPhone: this.nullIfBlank(this.bookingForm.value.passengerPhone)
    };

    if (this.selectedSeats.length === seatsBooked) {
      payload.seatNumbers = this.selectedSeats;
    }

    this.isBooking = true;
    this.publicBookingService.bookTrip(this.getTripId(this.selectedTrip), payload).pipe(
      finalize(() => this.isBooking = false)
    ).subscribe({
      next: confirmation => {
        this.confirmation = this.enrichConfirmation(confirmation, payload);
        this.lookupResult = null;
        this.selectedSeats = [];
        this.seatSelectionMessage = '';
        this.resetBookingForm();
      },
      error: error => {
        this.bookingError = this.extractError(error, 'Unable to complete booking. Please try again.');
        if (this.isSeatConflict(error)) {
          this.bookingError = 'One or more selected seats were just booked. Choose another available seat.';
          this.refreshSelectedTrip();
        }
      }
    });
  }

  lookupBooking(): void {
    const reference = String(this.lookupForm.value.bookingReference || '').trim();
    this.lookupError = '';
    this.lookupResult = null;

    if (!reference) {
      this.lookupError = 'Enter a booking reference.';
      return;
    }

    this.publicBookingService.getBooking(reference).subscribe({
      next: result => this.lookupResult = result,
      error: error => this.lookupError = this.extractError(error, 'Booking reference not found.')
    });
  }

  refreshSelectedTrip(): void {
    if (this.selectedTrip) {
      this.viewTrip(this.selectedTrip);
    }
  }

  getOperatorName(operator: PublicOperator): string {
    return operator.operatorName || operator.name || operator.orgName || operator.organisationName || `Operator ${this.getOperatorId(operator) || ''}`.trim();
  }

  getOperatorId(operator: PublicOperator): number | null {
    return this.pickNumber(operator, ['operatorId', 'operatorID', 'organisationID', 'organisationId']);
  }

  getTripId(trip: PublicTrip): number {
    return this.pickNumber(trip, ['tripID', 'tripId']) || 0;
  }

  getTripOperatorName(trip: PublicTrip): string {
    return trip.operatorName || trip.organisationName || 'Operator';
  }

  getTripRouteName(trip: PublicTrip): string {
    return trip.routeName || trip.tripName || 'Route';
  }

  getTripVehicleLabel(trip: PublicTrip): string {
    return this.combineVehicleIdentity(trip.vehicleIdentificationNumber, trip.vehicleRegistration)
      || trip.vehicleLabel
      || 'Vehicle details pending';
  }

  getTripStart(trip: PublicTrip): string {
    return trip.tripStartTime || trip.departureTime || '';
  }

  getTripEnd(trip: PublicTrip): string {
    return trip.tripEndTime || trip.arrivalTime || '';
  }

  getSeatsAvailable(trip: PublicTrip): number {
    return Number(trip.tripSeatsAvailable ?? trip.seatsAvailable ?? 0);
  }

  getTripCapacity(trip: PublicTrip): number {
    return Number(trip.tripCapacity || Math.max(this.getSeatsAvailable(trip), this.getBookedSeats(trip).length, 0));
  }

  getBookedSeats(trip: PublicTrip): number[] {
    return Array.isArray(trip.bookedSeatNumbers) ? trip.bookedSeatNumbers.map(Number).filter(Boolean) : [];
  }

  getOrderedStops(trip: PublicTrip | null): PublicTripStop[] {
    return this.extractStops(trip).sort((a, b) => this.getStopOrder(a) - this.getStopOrder(b));
  }

  getKeyStops(trip: PublicTrip): string {
    return this.getOrderedStops(trip).slice(0, 4).map(stop => this.getStopName(stop)).filter(Boolean).join(' - ') || 'Stops pending';
  }

  getStopName(stop: PublicTripStop): string {
    const source = stop as any;
    return source.stopName || source.StopName || source.name || source.Name || 'Stop';
  }

  getStopOrder(stop: PublicTripStop): number {
    const source = stop as any;
    return Number(source.stopOrder ?? source.StopOrder ?? source.order ?? source.Order ?? 0);
  }

  getSeatNumbers(): number[] {
    const capacity = this.selectedTrip ? this.getTripCapacity(this.selectedTrip) : 0;
    return Array.from({ length: capacity }, (_, index) => index + 1);
  }

  isSeatBooked(seatNumber: number): boolean {
    return this.selectedTrip ? this.getBookedSeats(this.selectedTrip).includes(seatNumber) : false;
  }

  isSeatSelected(seatNumber: number): boolean {
    return this.selectedSeats.includes(seatNumber);
  }

  hasPassengerContact(): boolean {
    return Boolean(String(this.bookingForm.value.passengerEmail || '').trim() || String(this.bookingForm.value.passengerPhone || '').trim());
  }

  getConfirmationReference(confirmation: PublicBookingConfirmation | null): string {
    return confirmation?.bookingReference || confirmation?.reference || '-';
  }

  getConfirmationSeats(confirmation: PublicBookingConfirmation | null): number[] {
    return confirmation?.selectedSeats || confirmation?.seatNumbers || [];
  }

  getConfirmationTicketUrl(confirmation: PublicBookingConfirmation | null): string {
    const rawUrl = confirmation?.eTicketUrl || confirmation?.ticketUrl || '';
    const token = this.extractTicketToken(rawUrl);
    return token ? `/tickets/${encodeURIComponent(token)}` : rawUrl;
  }

  getConfirmationTripName(confirmation: PublicBookingConfirmation | null): string {
    const trip = confirmation?.trip || this.selectedTrip;
    return trip ? this.getTripRouteName(trip) : 'Trip';
  }

  getConfirmationDeparture(confirmation: PublicBookingConfirmation | null): string {
    const trip = confirmation?.trip || this.selectedTrip;
    return trip ? this.getTripStart(trip) : '';
  }

  private currentFilters(): PublicTripSearchFilters {
    const routeOrStop = String(this.searchForm.value.routeOrStop || '').trim();
    return {
      operatorId: this.searchForm.value.operatorId,
      operatorName: this.nullIfBlank(this.searchForm.value.operatorName),
      q: this.nullIfBlank(this.searchForm.value.q),
      route: routeOrStop || null,
      stop: routeOrStop || null,
      date: this.nullIfBlank(this.searchForm.value.date),
      time: this.nullIfBlank(this.searchForm.value.time)
    };
  }

  private orderTrips(trips: PublicTrip[]): PublicTrip[] {
    return [...trips].sort((a, b) => new Date(this.getTripStart(a) || 0).getTime() - new Date(this.getTripStart(b) || 0).getTime());
  }

  private isSeatConflict(error: any): boolean {
    const message = this.extractError(error, '').toLowerCase();
    return error?.status === 409 || message.includes('seat') || message.includes('available') || message.includes('conflict');
  }

  private extractError(error: any, fallback: string): string {
    return error?.error?.message
      || error?.error?.title
      || (typeof error?.error === 'string' ? error.error : '')
      || error?.message
      || fallback;
  }

  private pickNumber(source: any, keys: string[]): number | null {
    for (const key of keys) {
      const value = source?.[key];
      const numeric = Number(value);
      if (value !== undefined && value !== null && Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }
    return null;
  }

  private nullIfBlank(value: unknown): string | null {
    const text = String(value || '').trim();
    return text || null;
  }

  private combineVehicleIdentity(identificationNumber?: string | null, registrationNumber?: string | null): string {
    return [identificationNumber, registrationNumber]
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .join(' / ');
  }

  private resetBookingForm(): void {
    this.bookingForm.reset({
      passengerName: this.getSignedInPassengerName(),
      passengerEmail: this.getSignedInPassengerEmail(),
      passengerPhone: this.getSignedInPassengerPhone(),
      seatsBooked: 1
    }, { emitEvent: false });
  }

  private enrichConfirmation(confirmation: PublicBookingConfirmation, payload: PublicBookingRequest): PublicBookingConfirmation {
    return {
      ...confirmation,
      passengerName: confirmation.passengerName || payload.passengerName,
      passengerEmail: confirmation.passengerEmail || payload.passengerEmail || undefined,
      passengerPhone: confirmation.passengerPhone || payload.passengerPhone || undefined,
      seatsBooked: confirmation.seatsBooked || payload.seatsBooked,
      selectedSeats: confirmation.selectedSeats || confirmation.seatNumbers || payload.seatNumbers || [],
      trip: confirmation.trip || this.selectedTrip || undefined
    };
  }

  private extractStops(trip: PublicTrip | null): PublicTripStop[] {
    if (!trip) {
      return [];
    }

    const source = trip as any;
    const candidates = [
      source.stops,
      source.Stops,
      source.routeStops,
      source.RouteStops,
      source.route?.stops,
      source.route?.Stops,
      source.route?.routeStops,
      source.route?.RouteStops,
      source.Route?.stops,
      source.Route?.Stops,
      source.Route?.routeStops,
      source.Route?.RouteStops
    ];

    for (const candidate of candidates) {
      const stops = this.asArray(candidate);
      if (stops.length) {
        return stops as PublicTripStop[];
      }
    }

    return [];
  }

  private asArray(value: unknown): unknown[] {
    if (Array.isArray(value)) {
      return value;
    }

    const collection = value as { $values?: unknown[] } | null | undefined;
    return Array.isArray(collection?.$values) ? collection.$values : [];
  }

  private extractTicketToken(url: string): string {
    const trimmedUrl = String(url || '').trim();
    if (!trimmedUrl) {
      return '';
    }

    const match = trimmedUrl.match(/\/tickets\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  }

  private getSignedInPassengerName(): string {
    const user = this.authService.getCurrentUser() as any;
    const fullName = [user?.userFirstName, user?.userLastName].map(value => String(value || '').trim()).filter(Boolean).join(' ');
    return fullName || user?.userName || user?.username || '';
  }

  private getSignedInPassengerEmail(): string {
    const user = this.authService.getCurrentUser() as any;
    const explicitEmail = String(user?.userEmail || user?.email || '').trim();
    const username = String(user?.username || user?.userName || '').trim();
    return explicitEmail || (username.includes('@') ? username : '');
  }

  private getSignedInPassengerPhone(): string {
    const user = this.authService.getCurrentUser() as any;
    return String(user?.phoneNumber || user?.userPhone || user?.phone || user?.contactPhone || '').trim();
  }
}
